/**
 * TanStack Query key factories. After mutations, invalidate the keys listed in the
 * invalidation matrix (see plan): equipment → equipment.all; programs → programs.all;
 * admin checklist CRUD → checklistItems.admin(true|false); assignTrainer → admin.clients.all;
 * etc. Import {@link staleTimeWithJitterFromKey} + {@link CATALOG_STALE_BASE_MS} from
 * ./query-cache for opted-in GETs. Session/dashboard/assigned/chat/notes/time-series stay staleTime: 0.
 */
export const queryKeys = {
  equipment: {
    all: ["equipment"] as const,
    options: ["equipment", "options"] as const,
  },
  exercises: {
    adminList: ["exercises", "admin-list"] as const,
    clientList: ["exercises", "client-list"] as const,
  },
  programs: {
    all: ["programs"] as const,
    detail: (id: string) => ["programs", "detail", id] as const,
  },
  clientPrograms: (profileId: string) => ["client-programs", profileId] as const,
  pricingTiers: {
    all: ["pricing-tiers"] as const,
  },
  lessons: {
    list: (includeDisabled: boolean) => ["lessons", "list", includeDisabled] as const,
    detail: (id: string) => ["lessons", "detail", id] as const,
  },
  filesById: (ids: string[]) => ["files", "by-id", [...ids].sort().join(",")] as const,
  workoutTemplates: {
    list: (difficulty?: string) => ["workout-templates", "list", difficulty ?? "All"] as const,
    meta: (id: string) => ["workout-templates", "meta", id] as const,
    groupings: (id: string) => ["workout-templates", "groupings", id] as const,
  },
  assessments: {
    adminList: (includeDeleted: boolean) => ["assessments", "admin", includeDeleted] as const,
    clientList: ["assessments", "client-list"] as const,
    detail: (id: string) => ["assessments", "detail", id] as const,
  },
  legal: {
    adminDocuments: ["legal", "admin-documents"] as const,
    current: (type: string) => ["legal", "current", type] as const,
    status: ["legal", "status"] as const,
  },
  faqs: {
    all: ["faqs"] as const,
  },
  prompts: {
    all: ["prompts"] as const,
  },
  emailTemplates: {
    all: ["email-templates"] as const,
  },
  checklistItems: {
    admin: (includeDeleted: boolean) => ["checklist-items", "admin", includeDeleted] as const,
    me: ["checklist-items", "me"] as const,
  },
  admin: {
    settings: ["admin", "settings"] as const,
    users: ["admin", "users"] as const,
    trainers: ["admin", "trainers"] as const,
    clients: {
      all: ["admin", "clients"] as const,
      profile: (clientId: string) => ["admin", "clients", clientId, "profile"] as const,
      checklistItems: (clientId: string) => ["admin", "clients", clientId, "checklist-items"] as const,
      files: (clientId: string) => ["admin", "clients", clientId, "files"] as const,
    },
    contactSubmissions: ["admin", "contact-submissions"] as const,
  },
  goals: {
    availableMetrics: ["goals", "available-metrics"] as const,
    enabledMetrics: ["goals", "enabled-metrics"] as const,
    adminEnabledMetrics: (profileId: string) => ["goals", "admin", profileId, "enabled-metrics"] as const,
  },
  onboarding: {
    me: ["onboarding", "me"] as const,
  },
} as const;
